import React, { Fragment, useEffect, useState } from 'react'
import {
  Autocomplete,
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@mui/material'
import AlertDialog from 'src/components/Dialog'
import { IUser } from 'src/services/IAM/AdminUser/AdminUser.interface'
import { useAdminGroup } from 'src/hooks/IAM/AdminGroups/useAdminGroups'
import { useAdminPermission } from 'src/hooks/IAM/AdminPermission/useAdminPermission'
import { useUserIAM } from 'src/hooks/IAM/AdminUser/useUserIAM'
import StepperModal from 'src/views/AdminUser/components/StepperModal'
import { INPermission } from 'src/services/IAM/AdminPermissions/AdminPermissions.interface'
import { usePagination } from 'src/hooks/IAM/Components/Pagination/usePagination'

// Definición de las columnas de la tabla
const columns = [
  { label: 'ID', minWidth: 170 },
  { label: 'Nombre', minWidth: 170 },
  { label: 'Descripcion', minWidth: 100 },
  { label: 'Acciones', minWidth: 100 },
]

/**
 * El componente `TableGroups` muestra una lista de grupos con funcionalidades para editar, 
 * eliminar y gestionar los permisos y usuarios de cada grupo.
 *
 * @returns {JSX.Element} El componente TableGroups.
 * @component
 * @example
 * ```tsx
 * import React from 'react'
 * import TableGroups from './TableGroups'
 *
 * const App = () => {
 *   return (
 *     <div>
 *       <TableGroups />
 *     </div>
 *   )
 * }
 * export default App
 * ```
 */
const TableGroups = (): JSX.Element => {
  // Hooks personalizados
  const {
    currentPage,
    handlePageChange,
    handleRowsPerPageChange,
    rowsPerPage,
    rowsPerPageOptions,
    totalItems,
  } = usePagination()

  const {
    groups,
    updatedGroup,
    setGroupAdmin,
    removeGroup,
    setUpdateGroup,
    updatedGroupAdmin,
  } = useAdminGroup()

  const { loadPermissions, permissions } = useAdminPermission()
  const { usersIAM } = useUserIAM()

  // Estado local para los permisos seleccionados
  const [selectedPermissions, setSelectedPermissions] = useState<INPermission[]>([])

  /**
   * Maneja la selección de permisos.
   * @param {INPermission} permission - El permiso seleccionado o deseleccionado.
   */
  const handlerSelectedPermissions = (permission: INPermission) => {
    console.log(permission, 'permission')

    const updatedPermission = {
      ...permission,
      active: !permission.active,
    }

    const newSelectedPermissions = selectedPermissions.filter(
      (p) => p.permissionId !== permission.permissionId,
    )

    if (updatedPermission.active) {
      setSelectedPermissions([...newSelectedPermissions, updatedPermission])
    } else {
      setSelectedPermissions(newSelectedPermissions)
    }
  }

  /**
   * Une los permisos seleccionados con los permisos existentes del grupo.
   * @returns {INPermission[]} - Los permisos combinados.
   */
  const joinPermissions = (): INPermission[] => {
    const permissionsJoined = permissions.map((p) => ({
      ...p,
      active: updatedGroup.permissions.some(
        (up) => up.permissionId === p.permissionId,
      )
        ? true
        : p.active,
    }))

    selectedPermissions.forEach((selected) => {
      const index = permissionsJoined.findIndex(
        (item) => item.permissionId === selected.permissionId,
      )
      if (index !== -1) {
        permissionsJoined[index].active = selected.active
      } else {
        permissionsJoined.push({ ...selected, active: false })
      }
    })

    return permissionsJoined
  }

  /**
   * Combina los permisos seleccionados y actualizados.
   * @returns {INPermission[]} - Lista única de permisos activos.
   */
  const jointSelectedPermissions = (): INPermission[] => {
    const joinedPermissions = joinPermissions().filter((e) => e.active)
    const combinedPermissions = [
      ...updatedGroup.permissions,
      ...joinedPermissions,
    ]

    const uniquePermissions = combinedPermissions.filter(
      (item, index, self) =>
        index === self.findIndex((t) => t.permissionId === item.permissionId),
    )

    return uniquePermissions.map((item) => ({
      ...item,
      active: true,
    }))
  }

  return (
    <Fragment>
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 440 }}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column, index) => (
                  <TableCell
                    key={index}
                    align={'center'}
                    style={{ minWidth: column.minWidth }}
                  >
                    <strong>{column.label}</strong>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((group, e) => {
                return (
                  <TableRow hover role="checkbox" tabIndex={-1} key={e}>
                    <TableCell align="center">{group.groupId}</TableCell>
                    <TableCell align="center">{group.groupName}</TableCell>
                    <TableCell align="center">{group.description}</TableCell>
                    <TableCell align="center">
                      <Stack
                        gap={2}
                        direction="row"
                        justifyContent={'flex-end'}
                      >
                        {/* Componente de diálogo para editar grupo */}
                        <AlertDialog
                          title={`Editar grupo "${updatedGroup.groupName}"`}
                          buttonLabel={'Editar'}
                          disagreeLabel="Cancelar"
                          agreeLabel="Editar"
                          onOpen={() => {
                            setUpdateGroup(group)
                          }}
                          onAgree={async () => {
                            updatedGroupAdmin(updatedGroup)
                          }}
                          children={
                            <form>
                              <Box>
                                {/* Campo para editar el nombre del grupo */}
                                <TextField
                                  sx={{ mt: 2 }}
                                  fullWidth
                                  id="outlined-basic"
                                  label="Nombre grupo"
                                  variant="outlined"
                                  size="small"
                                  value={updatedGroup?.groupName || ''}
                                  required
                                  onChange={(e) => {
                                    setUpdateGroup({
                                      ...updatedGroup,
                                      groupName: e.target.value,
                                    })
                                  }}
                                />
                                {/* Autocompletado para seleccionar usuarios */}
                                <Autocomplete
                                  disablePortal
                                  id="combo-box-demo"
                                  options={usersIAM ?? null}
                                  sx={{ mt: 2 }}
                                  className="form-group-input"
                                  size="small"
                                  fullWidth
                                  multiple
                                  value={updatedGroup?.users ?? []}
                                  onChange={(event, newValue: IUser[]) => {
                                    setUpdateGroup({
                                      ...updatedGroup,
                                      users: newValue,
                                    })
                                  }}
                                  isOptionEqualToValue={(option, value) => {
                                    return option.email === value.email
                                  }}
                                  getOptionLabel={(option) => `${option.userDocType} - ${option.userDocId}`}
                                  renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option: IUser, index) => (
                                      <Chip
                                        label={`${option.userDocType} - ${option.userDocId}`}
                                        {...getTagProps({ index })}
                                        key={index}
                                      />
                                    ))
                                  }
                                  renderInput={(params) => (
                                    <TextField {...params} label="Usuario" />
                                  )}
                                />
                                <Stack sx={{ mt: 2 }}>
                                  {/* Componente de diálogo para editar permisos */}
                                  <AlertDialog
                                    title="Permisos"
                                    buttonLabel={`Editar permisos ${updatedGroup.permissions.length}`}
                                    maxWidth="lg"
                                    agreeLabel="Confirmar"
                                    disagreeLabel="Cancelar"
                                    onAgree={() => {
                                      updatedGroupAdmin({
                                        ...updatedGroup,
                                        permissions: jointSelectedPermissions(),
                                      })
                                    }}
                                    children={
                                      <Stack direction={'row'}>
                                        <StepperModal
                                          loadPermissions={loadPermissions}
                                          permissionsProp={joinPermissions()}
                                          selectedPermissions={jointSelectedPermissions()}
                                          handlerSelectedPermissions={
                                            handlerSelectedPermissions
                                          }
                                        />
                                      </Stack>
                                    }
                                  />
                                </Stack>
                              </Box>
                            </form>
                          }
                        />
                        {/* Componente de diálogo para eliminar grupo */}
                        <AlertDialog
                          buttonLabel={'eliminar'}
                          title={`¿Estás seguro de eliminar el grupo "${updatedGroup.groupName}"?`}
                          disagreeLabel="Cancelar"
                          agreeLabel="eliminar"
                          color="error"
                          onOpen={() => {
                            setUpdateGroup(group)
                          }}
                          onAgree={() => {
                            removeGroup(Number(group.groupId))
                          }}
                          nameToDelete={updatedGroup.groupName}
                          withConfirmation
                          onDisagree={() => { }}
                          children={
                            <div>
                              <p>
                                Al eliminar el grupo
                                <strong> {updatedGroup.groupName}</strong>, se perderán los permisos asignados.
                              </p>
                            </div>
                          }
                        />
                      </Stack>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>
    </Fragment>
  )
}

export default TableGroups
